import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { CreateUnitWizard } from '../components/CreateUnitWizard';
import { CreateAssessmentForm } from '../components/CreateAssessmentForm';
import { AddTopicsWizardDialog } from '../components/LessonsPage/components/AddTopicsWizardDialog';
import type { AddTopicsJob } from '../components/LessonsPage/utils/groupTopicsForStudentCurriculumAdd';
import { CreateYourOwnWizardDialog } from '../components/LessonsPage/components/CreateYourOwnWizardDialog';
import { CreateCustomTopicsDialog } from '../components/LessonsPage/components/CreateCustomTopicsDialog';
import { AddCustomTopicDialog } from '../components/LessonsPage/components/AddCustomTopicDialog';
import { AssessmentEditorContent } from './curriculum/AssessmentEditorPage';
import { ReviewUnitsPanel } from '../components/ReviewUnitsPanel';
import { InProgressUnitsPanel } from '../components/InProgressUnitsPanel';
import { CompletedUnitsPanel } from '../components/CompletedUnitsPanel';
import { useSelectedStudent } from '../hooks/useSelectedStudent';
import { useLessonsPageState } from '../components/LessonsPage/hooks/useLessonsPageState';
import { useLessonsPageCommands } from '../components/LessonsPage/hooks/useLessonsPageCommands';
import { useLessonsPageEvents } from '../components/LessonsPage/hooks/useLessonsPageEvents';
import { LessonsPageHeader } from '../components/LessonsPage/components/LessonsPageHeader';
import { LessonsPageTabs } from '../components/LessonsPage/components/LessonsPageTabs';
import { CreateUnitsTabContent } from '../components/LessonsPage/components/CreateUnitsTabContent';
import { AddTopicsTabContent } from '../components/LessonsPage/components/AddTopicsTabContent';
import { TopicLimitDialog } from '../components/LessonsPage/components/TopicLimitDialog';
import { RemoveTopicsFromStudentCurriculumDialog } from '../components/LessonsPage/components/RemoveTopicsFromStudentCurriculumDialog';
import { DeleteTenantCustomCurriculumTopicsDialog } from '../components/LessonsPage/components/DeleteTenantCustomCurriculumTopicsDialog';
import {
    viewStates,
    unitsViewStates,
    topicLimitDialogViewStates,
    messages,
    maxTopicsPerUnit,
    maxTopicsPerAddToCurriculum
} from '../components/LessonsPage/constants';
import {
    getLeafTopics,
    isTopicInStudentCurriculumForSelectedStudent,
    collectLeafTopics,
    getTopicId,
    canDeleteSelectedTenantCustomCurriculumTopics,
    isTenantCustomCatalogProvider,
    getCreateUnitCompatibilityFromTreeSelection,
    mapTreeSelectionToCreateUnitTopicChoices
} from '../components/LessonsPage/utils/topicUtils';
import {
    collectStudentSubjectIdsForSelectedTopics,
    extractTopicMetadata,
    buildBreadcrumb,
    getSelectedProviderName,
    getProviderName,
    computeStudentCurriculumExpandNodesForPlacements,
    type CurriculumPlacementKey
} from '../components/LessonsPage/utils/curriculumUtils';
import { getUnitsForTab } from '../components/LessonsPage/utils/unitUtils';
import { lessonTabForUnitBackendState } from '../components/LessonsPage/utils/lessonUnitNavigation';

export function LessonsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const state = useLessonsPageState();
    const [showRemoveTopicsConfirmDialog, setShowRemoveTopicsConfirmDialog] = useState(false);
    const [isRemovingTopicsFromCurriculum, setIsRemovingTopicsFromCurriculum] = useState(false);
    const [showDeleteTenantCustomCurriculumTopicsDialog, setShowDeleteTenantCustomCurriculumTopicsDialog] = useState(false);
    const commands = useLessonsPageCommands();
    const addTopicsWizardBatchingRef = useRef(false);
    const createYourOwnWizardActiveRef = useRef(false);
    const pendingAddTopicsExpandPlacementsRef = useRef<CurriculumPlacementKey[] | null>(null);
    const skipCreateTabExpandClearRef = useRef(false);
    const [topicLimitMessage, setTopicLimitMessage] = useState<string | undefined>(undefined);
    const [showCreateUnitWizard, setShowCreateUnitWizard] = useState(false);

    const createUnitTreeSeed = useMemo(() => {
        const compatibility = getCreateUnitCompatibilityFromTreeSelection(
            state.studentCurriculumData,
            state.selectedTopics,
            extractTopicMetadata,
            maxTopicsPerUnit
        );
        if (!compatibility.canCreateUnit || !compatibility.unitCreationMode) {
            return { mode: null as 'SingleSubject' | 'Blended' | null, topics: [] as ReturnType<typeof mapTreeSelectionToCreateUnitTopicChoices> };
        }
        return {
            mode: compatibility.unitCreationMode,
            topics: mapTreeSelectionToCreateUnitTopicChoices(
                state.studentCurriculumData,
                state.selectedTopics,
                extractTopicMetadata
            )
        };
    }, [state.studentCurriculumData, state.selectedTopics]);

    const sendGetEducationSystemDefinitionsRef = useRef(commands.sendGetEducationSystemDefinitionsCommand);
    sendGetEducationSystemDefinitionsRef.current = commands.sendGetEducationSystemDefinitionsCommand;
    const loadEducationSystemDefinitions = useCallback(() => {
        void sendGetEducationSystemDefinitionsRef.current?.({});
    }, []);

    const sendGetEducationSystemsRef = useRef(commands.sendGetEducationSystemsCommand);
    sendGetEducationSystemsRef.current = commands.sendGetEducationSystemsCommand;
    const loadEducationSystems = useCallback(() => {
        void sendGetEducationSystemsRef.current?.({});
    }, []);

    const {
        selectedStudentId,
        students,
        isLoading: isStudentLoading,
        selectedStudentPassFailThreshold,
        selectedStudentFirstName,
        selectedStudentEducationSystemKey
    } = useSelectedStudent();

    const planPageTitle = selectedStudentFirstName
        ? `${selectedStudentFirstName}'s Plan`
        : messages.pageTitle;

    const [lessonUnitFocus, setLessonUnitFocus] = useState<{ unitId: string; token: number } | null>(null);
    const [sessionViewLessonTitle, setSessionViewLessonTitle] = useState<string | null>(null);
    const isSessionViewActive = sessionViewLessonTitle != null;

    const clearLessonUnitFocus = useCallback(() => {
        setLessonUnitFocus(null);
    }, []);

    const handleOpenUnitFromPlan = useCallback(
        (unit: any) => {
            const uid = unit?.e251_UnitId;
            if (typeof uid !== 'string' || uid.trim().length === 0) return;
            state.setActiveTab(lessonTabForUnitBackendState(unit?.e251_State));
            setLessonUnitFocus({ unitId: uid.trim(), token: Date.now() });
        },
        [state.setActiveTab]
    );

    const handleTopicsRemovedFromStudentCurriculum = useCallback(() => {
        setIsRemovingTopicsFromCurriculum(false);
        setShowRemoveTopicsConfirmDialog(false);
    }, []);

    // Set up events
    useLessonsPageEvents(
        state,
        commands,
        selectedStudentId,
        state.lessonsPanelRef,
        addTopicsWizardBatchingRef,
        pendingAddTopicsExpandPlacementsRef,
        handleTopicsRemovedFromStudentCurriculum,
        createYourOwnWizardActiveRef
    );

    // Update ref when isStudentCurriculumActive changes
    useEffect(() => {
        state.isStudentCurriculumActiveRef.current = state.isStudentCurriculumActive;
    }, [state.isStudentCurriculumActive]);

    // Load data when student is selected or changes - WAIT for student validation to complete
    useEffect(() => {
        // Don't fire queries until student validation is complete
        if (isStudentLoading || !selectedStudentId) return;
        
        // Load units when student changes
        state.setUnitsViewState(unitsViewStates.loading);
        commands.sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
    }, [isStudentLoading, selectedStudentId]);

    // Load curriculum when student changes - WAIT for student validation to complete
    useEffect(() => {
        // Don't fire queries until student validation is complete
        if (isStudentLoading || !selectedStudentId) return;
        
        state.setViewState(viewStates.loadingCurriculum);
        // Clear selected topics, curriculum data, and expanded nodes when switching students
        state.setSelectedTopics([]);
        state.setCurriculumData(null); // Clear to prevent stale data
        state.setStudentCurriculumData(null); // So Add Topics disabled state cannot use another student’s E180
        state.setExpandedNodes(new Set()); // Clear expanded state when switching views
        state.setLoadedNodeIds(new Set()); // Clear loaded nodes
        state.setLoadingNodeIds(new Set()); // Clear loading nodes
        
        // Always load student curriculum (used for Add Topics disabled state and Create tab)
        commands.sendGetStudentCurriculumCommand({ C260_StudentId: selectedStudentId });
    }, [isStudentLoading, selectedStudentId]);

    // Create vs Add Topics: tree mode and provider default
    useEffect(() => {
        if (isStudentLoading || !selectedStudentId) return;

        if (state.activeTab === 'addTopics') {
            state.setIsStudentCurriculumActive(false);
            state.isStudentCurriculumActiveRef.current = false;
            if (!state.selectedProvider) {
                state.setSelectedProvider('oak');
            }
        } else if (state.activeTab === 'create') {
            state.setIsStudentCurriculumActive(true);
            state.isStudentCurriculumActiveRef.current = true;
        }
    }, [isStudentLoading, selectedStudentId, state.activeTab]);

    // Provider curriculum when on Add Topics tab
    useEffect(() => {
        if (isStudentLoading || !selectedStudentId) return;
        if (state.activeTab !== 'addTopics') return;

        const providerKey = state.selectedProvider || 'oak';
        // Clear stale E180-shaped or prior-provider data so the library tree stays in
        // loading until E179 returns (avoids empty/noData flash then "pop in").
        state.setCurriculumData(null);
        state.setViewState(viewStates.loadingCurriculum);
        commands.sendGetStudentCurriculumCommand({ C260_StudentId: selectedStudentId });
        commands.sendCurriculumCommand({
            c258_CurriculumProviderKey: providerKey
        });
        commands.sendGetTenantCustomCurriculumCommand({});
    }, [isStudentLoading, selectedStudentId, state.activeTab, state.selectedProvider]);

    // Reset selection when switching between Create and Add Topics
    useEffect(() => {
        if (state.activeTab !== 'create' && state.activeTab !== 'addTopics') return;
        state.setSelectedTopics([]);
        state.setExpandedCards(new Set());
        if (state.activeTab === 'create' && skipCreateTabExpandClearRef.current) {
            skipCreateTabExpandClearRef.current = false;
        } else {
            state.setExpandedNodes(new Set());
            state.setLoadedNodeIds(new Set());
            state.setLoadingNodeIds(new Set());
        }
    }, [state.activeTab]);

    // Determine active tab from URL
    useEffect(() => {
        const path = location.pathname;
        
        if (path.includes('/assess')) {
            state.setActiveTab('assess');
        } else if (path.includes('/add-topics')) {
            state.setActiveTab('addTopics');
        } else if (path.includes('/create')) {
            state.setActiveTab('create');
        } else if (path.includes('/review')) {
            state.setActiveTab('review');
        } else if (path.includes('/inprogress')) {
            state.setActiveTab('inprogress');
        } else if (path.includes('/completed')) {
            state.setActiveTab('done');
        } else if (path === '/app/lessons' || path === '/app/lessons/') {
            // Default to Create Units tab for base route
            state.setActiveTab('create');
        }
    }, [location.pathname]);

    // Handler functions
    const handleApproveUnit = async (unitId: string) => {
        await commands.sendApproveUnitCommand({ C264_UnitId: unitId });
    };

    const handleDeleteUnit = async (unitId: string) => {
        // Delete is now handled by DeleteUnitDialog component
        // This function is kept for compatibility but the dialog handles the actual deletion
    };

    const handleRegenerateUnit = async (unitId: string) => {
        await commands.sendRegenerateUnitCommand({ C266_UnitId: unitId });
    };

    const handleStartLesson = async (unitId: string, lessonId: string, lesson: any) => {
        // Start is handled by StartLessonDialog component
        // This is just a placeholder for the prop
    };

    const handleContinueLesson = async (unitId: string, lessonId: string, lesson: any) => {
        // Continue is handled by LessonSessionDialog component
        // This is just a placeholder for the prop
    };

    const handleMarkUnitTouched = async (unitId: string) => {
        await commands.sendMarkUnitTouchedCommand({ C281_UnitId: unitId });
    };

    const handleCreateAssessment = (unitId: string, topicIds: string[], unitTitle?: string) => {
        // Open assessment creation form with unit context
        state.setAssessmentFormContext({ unitId, topicIds, studentId: selectedStudentId || undefined, unitTitle });
        state.setShowCreateAssessmentForm(true);
    };

    const handleRefresh = () => {
        if (!selectedStudentId) return;
        // AssessmentCard calls this from E172/E174/E180/E270 (and delete success). Keep wiring
        // onRefresh down to the card — optimistic LessonsPage patches alone are not enough for
        // live assessment status (Jul 2026). Silent GetUnits; no loading flash.
        commands.sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
    };

    const handleLoadNodeChildren = (nodeId: string, nodeType: 'year' | 'topic') => {
        if (nodeType !== 'year') {
            return;
        }
        if (state.isStudentCurriculumActive) {
            if (!selectedStudentId) {
                return;
            }
        } else if (state.selectedProvider) {
            if (!selectedStudentId) {
                console.warn("Cannot load curriculum node children without selected student");
                return;
            }
        } else {
            return;
        }

        state.setLoadingNodeIds(prev => {
            const newSet = new Set(prev);
            newSet.add(nodeId);
            return newSet;
        });

        if (state.isStudentCurriculumActive) {
            commands.sendGetStudentCurriculumNodeChildrenCommand({
                C287_StudentId: selectedStudentId!,
                C287_NodeId: nodeId,
                C287_NodeType: 'year'
            });
        } else {
            commands.sendGetCurriculumNodeChildrenCommand({
                C285_CurriculumProviderKey: state.selectedProvider!,
                C285_NodeId: nodeId,
                C285_NodeType: 'year',
                C285_StudentId: selectedStudentId!
            });
        }
    };

    const handleTreeNodeSelect = (node: any) => {
        if (node.type === 'topic' && node.data) {
            const topicId = node.data.e179_TopicId || node.data.e180_TopicId;
            
            // For library view: prevent selecting topics that are already in student curriculum
            // For student curriculum view: allow selection (topics can be selected for removal or lesson creation)
            if (!state.isStudentCurriculumActive && isTopicInStudentCurriculumForSelectedStudent(topicId, state.studentCurriculumData, selectedStudentId)) {
                return;
            }
            
            // Note: Selection of parent topics that haven't been expanded is prevented in CurriculumTreeView.selectNode
            
            // Unified behavior: both student curriculum and library view work the same way
            // - New topics are added to the TOP of the list
            // - When selecting, collect all leaf topics
            // - Collapse all cards when new topics are added
            
            let newLeafTopics: any[] = [];
            let shouldCollapseCards = false;
            
            if (state.isStudentCurriculumActive) {
                // Student curriculum: collect leaf topics (same as library view)
                // For student curriculum, topics are typically leaf nodes, but handle nested topics if they exist
                const metadata = extractTopicMetadata(node.data, state.studentCurriculumData);
                const providerKey = node.data.e180_ProviderKey || node.data.e179_ProviderKey;
                const leafTopics = collectLeafTopics(node.data, metadata, providerKey);
                
                // Check if all leaf topics are already selected
                const allLeafTopicsSelected = leafTopics.length > 0 && leafTopics.every(leafTopic => {
                    const ltId = leafTopic.e179_TopicId || leafTopic.e180_TopicId;
                    return state.selectedTopics.some(selectedTopic => 
                        (selectedTopic.e179_TopicId === ltId) || (selectedTopic.e180_TopicId === ltId)
                    );
                });
                
                if (allLeafTopicsSelected) {
                    // Deselect: remove all leaf topics
                    const leafTopicIds = new Set(leafTopics.map(lt => lt.e179_TopicId || lt.e180_TopicId));
                    state.setSelectedTopics(prev => prev.filter(topic => {
                        const tId = topic.e179_TopicId || topic.e180_TopicId;
                        return !leafTopicIds.has(tId);
                    }));
                } else {
                    // Select: add all leaf topics that aren't already selected to the TOP
                    const existingTopicIds = new Set(state.selectedTopics.map(t => t.e179_TopicId || t.e180_TopicId));
                    newLeafTopics = leafTopics.filter(lt => {
                        const ltId = lt.e179_TopicId || lt.e180_TopicId;
                        return !existingTopicIds.has(ltId);
                    });
                    shouldCollapseCards = newLeafTopics.length > 0;
                    state.setSelectedTopics(prev => [...newLeafTopics, ...prev]);
                }
            } else {
                // Library view: collect all leaf topics (existing behavior)
                const metadata = extractTopicMetadata(node.data, state.curriculumData);
                const providerKey = node.data.e179_ProviderKey || (!state.isStudentCurriculumActive && state.selectedProvider ? state.selectedProvider : undefined);
                const leafTopics = collectLeafTopics(node.data, metadata, providerKey);
                
                // Check if all leaf topics are already selected
                const allLeafTopicsSelected = leafTopics.length > 0 && leafTopics.every(leafTopic => {
                    const ltId = leafTopic.e179_TopicId || leafTopic.e180_TopicId;
                    return state.selectedTopics.some(selectedTopic => 
                        (selectedTopic.e179_TopicId === ltId) || (selectedTopic.e180_TopicId === ltId)
                    );
                });
                
                if (allLeafTopicsSelected) {
                    // Deselect: remove all leaf topics
                    const leafTopicIds = new Set(leafTopics.map(lt => lt.e179_TopicId || lt.e180_TopicId));
                    state.setSelectedTopics(prev => prev.filter(topic => {
                        const tId = topic.e179_TopicId || topic.e180_TopicId;
                        return !leafTopicIds.has(tId);
                    }));
                } else {
                    // Select: add all leaf topics that aren't already selected to the TOP
                    const existingTopicIds = new Set(state.selectedTopics.map(t => t.e179_TopicId || t.e180_TopicId));
                    newLeafTopics = leafTopics.filter(lt => {
                        const ltId = lt.e179_TopicId || lt.e180_TopicId;
                        return !existingTopicIds.has(ltId);
                    });
                    if (newLeafTopics.length > 0) {
                        const nextCount =
                            getLeafTopics(state.selectedTopics).length + newLeafTopics.length;
                        if (
                            state.activeTab === 'create' &&
                            nextCount > maxTopicsPerUnit
                        ) {
                            setTopicLimitMessage(undefined);
                            state.setShowLimitDialog(true);
                            return;
                        }
                        if (
                            state.activeTab === 'addTopics' &&
                            nextCount > maxTopicsPerAddToCurriculum
                        ) {
                            setTopicLimitMessage(messages.topicLimitMessageAddToCurriculum);
                            state.setShowLimitDialog(true);
                            return;
                        }
                    }
                    shouldCollapseCards = newLeafTopics.length > 0;
                    state.setSelectedTopics(prev => [...newLeafTopics, ...prev]);
                }
            }
            
            // Collapse all cards when new topics are added
            if (shouldCollapseCards) {
                state.setExpandedCards(new Set());
            }
        }
    };

    const handleRemoveTopic = (index: number) => {
        // Get the filtered leaf topics to find the topic at this index
        const leafTopics = getLeafTopics(state.selectedTopics);
        const topicToRemove = leafTopics[index];
        
        if (topicToRemove) {
            const topicId = getTopicId(topicToRemove);
            if (topicId) {
                // Remove by topicId from the full selectedTopics array
                state.setSelectedTopics(prev => prev.filter(topic => {
                    const tId = getTopicId(topic);
                    return tId !== topicId;
                }));
                state.setExpandedCards(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(topicId);
                    return newSet;
                });
            }
        }
    };

    const handleToggleCard = (topicId: string) => {
        state.setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(topicId)) {
                newSet.delete(topicId);
            } else {
                newSet.add(topicId);
            }
            return newSet;
        });
    };

    const handleCreateUnit = (request: import('../components/CreateUnitForm/createUnitTypes').CreateUnitRequest) => {
        state.setHasAttemptedSubmit(true);

        if (request.topicIds.length === 0) {
            console.error(messages.validationErrors.noTopicsSelected);
            return;
        }

        if (request.unitCreationMode === 'SingleSubject' && request.topicIds.length > maxTopicsPerUnit) {
            console.error(messages.topicLimitMessage);
            return;
        }

        if (!request.studentId || request.studentId.trim() === '') {
            console.error("Student must be selected");
            return;
        }

        const isExternal =
            request.contentSource === 'ExternalBook' || request.contentSource === 'ExternalOnline';

        if (!isExternal && request.includeApplicationLesson && request.learningStyles.length === 0) {
            console.error("At least one learning style must be selected");
            return;
        }

        const topicIds = request.topicIds;

        if (isExternal) {
            commands.sendCreateLessonCommand({
                c250_TopicIds: topicIds,
                c250_StudentId: request.studentId,
                c250_ContentSource: request.contentSource,
                c250_ExternalProviderId: request.externalProviderId,
                c250_ManualLessonEntries: (request.manualLessonEntries ?? []).map(e => ({
                    c250_Title: e.title,
                    c250_Summary: e.summary || null
                })),
                c250_SourceBlobIds: (request.sourceBlobIds ?? []).map(id => id),
                c250_UnitCreationMode: request.unitCreationMode,
                c250_IncludeApplicationLesson: request.includeApplicationLesson
            });
            return;
        }

        const materialsPrefs = request.materialsPrefs!;
        commands.sendCreateLessonCommand({
            c250_TopicIds: topicIds,
            c250_StudentId: request.studentId,
            c250_ContentSource: request.contentSource,
            ...(request.includeApplicationLesson ? { c250_LearningStyles: request.learningStyles } : {}),
            c250_SpecialInstructions: request.specialInstructions || null,
            c250_UseWorksheets: materialsPrefs.useWorksheets,
            c250_ReadingAudience: materialsPrefs.readingAudience,
            c250_ReadingDepth: materialsPrefs.readingDepth,
            c250_IncludeVideos: materialsPrefs.includeVideos,
            c250_MinimalWordsMin: materialsPrefs.minimalWordsMin,
            c250_MinimalWordsMax: materialsPrefs.minimalWordsMax,
            c250_ModerateWordsMin: materialsPrefs.moderateWordsMin,
            c250_ModerateWordsMax: materialsPrefs.moderateWordsMax,
            c250_ComprehensiveWordsMin: materialsPrefs.comprehensiveWordsMin,
            c250_ComprehensiveWordsMax: materialsPrefs.comprehensiveWordsMax,
            c250_UnitCreationMode: request.unitCreationMode,
            c250_IncludeApplicationLesson: request.includeApplicationLesson
        });
    };

    const handleAddTopics = () => {
        state.setShowAddTopicsDialog(true);
        state.setViewState(viewStates.addingTopics);
    };

    const handleOpenDeleteTenantCustomCurriculumTopics = () => {
        if (!selectedStudentId) return;
        if (
            !canDeleteSelectedTenantCustomCurriculumTopics(
                state.selectedTopics,
                state.studentCurriculumData,
                selectedStudentId
            )
        ) {
            return;
        }
        setShowDeleteTenantCustomCurriculumTopicsDialog(true);
        state.setViewState(viewStates.addingTopics);
    };

    const handleConfirmDeleteTenantCustomCurriculumTopics = () => {
        if (!selectedStudentId) return;
        const ids = getLeafTopics(state.selectedTopics)
            .map((t: any) => t.e179_TopicId || t.e180_TopicId)
            .filter((id: any) => id != null && String(id).trim() !== '');
        if (ids.length === 0) return;
        void commands.sendDeleteTenantCustomCurriculumTopicsCommand({
            c317_StudentId: selectedStudentId,
            c317_CatalogTopicIds: ids
        });
        state.setSelectedTopics([]);
        state.setViewState(viewStates.curriculumReady);
    };

    const handleOpenCreateYourOwnWizard = () => {
        state.setShowCreateYourOwnWizard(true);
        state.setViewState(viewStates.addingTopics);
    };

    const handleCloseCreateYourOwnWizard = () => {
        state.setShowCreateYourOwnWizard(false);
        state.setViewState(viewStates.curriculumReady);
    };

    const handleOpenSingleTopicFromWizard = (catalogSubjectId: string) => {
        state.setWizardInitialCatalogSubjectId(catalogSubjectId);
        state.setShowAddCustomTopicDialog(true);
        state.setViewState(viewStates.addingTopics);
    };

    const handleOpenBatchTopicsFromWizard = (catalogSubjectId: string) => {
        state.setWizardInitialCatalogSubjectId(catalogSubjectId);
        state.setShowCreateCustomTopicsDialog(true);
        state.setViewState(viewStates.addingTopics);
    };

    const handleCloseAddCustomTopicDialog = () => {
        state.setShowAddCustomTopicDialog(false);
        state.setWizardInitialCatalogSubjectId('');
        state.setViewState(viewStates.curriculumReady);
    };

    const handleCloseCreateCustomTopicsDialog = () => {
        state.setShowCreateCustomTopicsDialog(false);
        state.setWizardInitialCatalogSubjectId('');
        state.setViewState(viewStates.curriculumReady);
    };

    const handleRunAddTopicsJob = (job: AddTopicsJob) => {
        if (!selectedStudentId) return;
        if (job.type === 'c310') {
            void commands.sendCopyTenantCustomCurriculumTopicsCommand({
                c310_StudentId: selectedStudentId,
                c310_CatalogTopicIds: job.topicIds,
                ...(job.placement
                    ? {
                          c310_PlacementKeyStageId: job.placement.keyStageId,
                          c310_PlacementYearId: job.placement.yearId
                      }
                    : {})
            });
        } else {
            void commands.sendAddTopicsCommand({
                c259_StudentId: selectedStudentId,
                c259_TopicIds: job.topicIds,
                c259_CurriculumProviderKey: job.providerKey,
                c259_SourceCatalogSubjectId: job.sourceCatalogSubjectId,
                c259_SourceEducationSystemKeyStageId: job.sourceKeyStageId,
                c259_SourceEducationSystemYearId: job.sourceYearId,
                ...(job.placement
                    ? {
                          c259_PlacementKeyStageId: job.placement.keyStageId,
                          c259_PlacementYearId: job.placement.yearId
                      }
                    : {})
            });
        }
    };

    const handleRemoveTopicById = (topicId: string) => {
        state.setSelectedTopics((prev) =>
            prev.filter((topic) => {
                const id = getTopicId(topic);
                return id !== topicId;
            })
        );
    };

    const handleEnsureCustomSubject = (command: { c316_SubjectName: string; c316_EducationSystemKey: string }) => {
        void commands.sendEnsureCustomSubjectInTenantCurriculumCommand(command);
    };

    const handleGetCustomSubjectTopicNames = (command: any) => {
        commands.sendGetCustomSubjectTopicNamesCommand({
            ...command
        });
    };

    const handleAddCustomSubjectTopicsBatch = (command: any) => {
        commands.sendAddCustomSubjectTopicsBatchCommand({
            ...command
        });
    };

    const handleRemoveTopicsFromStudentCurriculum = () => {
        if (!selectedStudentId) return;

        // Student curriculum uses E180 structure, so only use e180_TopicId
        const topicIds = state.selectedTopics
            .map(topic => topic.e180_TopicId)
            .filter((id): id is string => !!id);

        const studentSubjectIds = collectStudentSubjectIdsForSelectedTopics(
            state.selectedTopics,
            state.studentCurriculumData,
            extractTopicMetadata
        );
        if (topicIds.length === 0 || studentSubjectIds.length === 0) {
            return;
        }

        setIsRemovingTopicsFromCurriculum(true);
        commands.sendRemoveTopicsCommand({
            c261_StudentId: selectedStudentId,
            c261_StudentSubjectIds: studentSubjectIds,
            c261_TopicIds: topicIds
        });
    };

    // Handle loading topic details
    const handleLoadTopicDetails = (topicId: string, providerKey?: string) => {
        // Validate topicId is a valid GUID (not a fallback like "topic-0")
        if (!topicId || topicId.startsWith('topic-')) {
            return; // Can't load details for topics without valid IDs
        }
        
        if (!providerKey && !state.selectedProvider) {
            // Try to get provider key from topic if it's in student curriculum
            const normalizeId = (id: any) => id?.toString().toLowerCase().trim();
            const normalizedTopicId = normalizeId(topicId);
            const topic = [...(state.curriculumData?.e179_Subjects || []), ...(state.studentCurriculumData?.e180_Subjects || [])]
                .flatMap((s: any) => (s.e179_KeyStages || s.e180_KeyStages || []))
                .flatMap((ks: any) => (ks.e179_Years || ks.e180_Years || []))
                .flatMap((y: any) => (y.e179_Topics || y.e180_Topics || []))
                .find((t: any) => {
                    const tId = normalizeId(t.e179_TopicId || t.e180_TopicId);
                    return tId === normalizedTopicId;
                });
            
            if (topic?.e179_ProviderKey || topic?.e180_ProviderKey) {
                providerKey = topic.e179_ProviderKey || topic.e180_ProviderKey;
            } else {
                return; // Can't load without provider key
            }
        }
        
        const providerKeyToUse = providerKey || state.selectedProvider;
        if (!providerKeyToUse) return;

        // Custom catalog ships with full topic payloads (E308/E180); C286 only loads built-in JSON.
        if (isTenantCustomCatalogProvider(providerKeyToUse)) {
            return;
        }
        
        // Add to loading set
        state.setLoadingTopicDetails(prev => {
            const newSet = new Set(prev);
            newSet.add(topicId);
            return newSet;
        });
        
        // Send command (topicId should be a valid GUID string)
        commands.sendGetTopicDetailsCommand({
            C286_CurriculumProviderKey: providerKeyToUse,
            C286_TopicId: topicId
        });
    };

    const buildBreadcrumbForTopic = (topicData: any): string => {
        return buildBreadcrumb(topicData, state.curriculumData);
    };

    return (
        <div className="p-4 sm:p-6">
                <LessonsPageHeader
                    planTitle={sessionViewLessonTitle ?? planPageTitle}
                />
                {!isSessionViewActive && (
                    <LessonsPageTabs activeTab={state.activeTab} />
                )}

                {/* Assess Tab Content */}
                {state.activeTab === 'assess' && (
                    <div className="sm:flex-1 sm:overflow-auto">
                        <AssessmentEditorContent/>
                    </div>
                )}

                {/* Create Units tab */}
                {state.activeTab === 'create' && (
                    <CreateUnitsTabContent
                        students={students}
                        selectedTopics={state.selectedTopics}
                        expandedCards={state.expandedCards}
                        expandedNodes={state.expandedNodes}
                        setExpandedNodes={state.setExpandedNodes}
                        curriculumData={state.curriculumData}
                        studentCurriculumData={state.studentCurriculumData}
                        setStudentCurriculumData={state.setStudentCurriculumData}
                        selectedProvider={state.selectedProvider}
                        backendLessons={state.backendLessons}
                        selectedStudentPassFailThreshold={selectedStudentPassFailThreshold}
                        loadedNodeIds={state.loadedNodeIds}
                        loadingNodeIds={state.loadingNodeIds}
                        loadingTopicDetails={state.loadingTopicDetails}
                        lessonsPanelRef={state.lessonsPanelRef}
                        mediumTreeviewRef={state.mediumTreeviewRef}
                        onTreeNodeSelect={handleTreeNodeSelect}
                        onLoadNodeChildren={handleLoadNodeChildren}
                        onRemoveTopic={handleRemoveTopic}
                        onToggleCard={handleToggleCard}
                        onOpenCreateUnitWizard={() => setShowCreateUnitWizard(true)}
                        onCreateAssessment={() => state.setShowCreateAssessmentForm(true)}
                        onOpenCreateYourOwnWizard={handleOpenCreateYourOwnWizard}
                        onRemoveTopicsFromStudentCurriculum={() => {
                            const leaves = getLeafTopics(state.selectedTopics).filter(
                                (t) => t?.e180_TopicId != null && String(t.e180_TopicId).trim() !== ''
                            );
                            if (leaves.length === 0) return;
                            setShowRemoveTopicsConfirmDialog(true);
                        }}
                        onAddTopics={handleAddTopics}
                        buildBreadcrumb={buildBreadcrumbForTopic}
                        getProviderName={getProviderName}
                        handleLoadTopicDetails={handleLoadTopicDetails}
                        setViewState={state.setViewState}
                        onClearAll={() => state.setSelectedTopics([])}
                        studentId={selectedStudentId}
                        isStudentLoading={isStudentLoading}
                        selectedStudentFirstName={selectedStudentFirstName}
                        onOpenUnitFromPlan={handleOpenUnitFromPlan}
                    />
                )}

                {/* Add Topics tab */}
                {state.activeTab === 'addTopics' && (
                    <AddTopicsTabContent
                        students={students}
                        selectedTopics={state.selectedTopics}
                        expandedCards={state.expandedCards}
                        expandedNodes={state.expandedNodes}
                        setExpandedNodes={state.setExpandedNodes}
                        curriculumData={state.curriculumData}
                        tenantCustomCurriculumLibraryData={state.tenantCustomCurriculumLibraryData}
                        studentCurriculumData={state.studentCurriculumData}
                        selectedProvider={state.selectedProvider}
                        backendLessons={state.backendLessons}
                        selectedStudentPassFailThreshold={selectedStudentPassFailThreshold}
                        loadedNodeIds={state.loadedNodeIds}
                        loadingNodeIds={state.loadingNodeIds}
                        loadingTopicDetails={state.loadingTopicDetails}
                        lessonsPanelRef={state.lessonsPanelRef}
                        mediumTreeviewRef={state.mediumTreeviewRef}
                        onTreeNodeSelect={handleTreeNodeSelect}
                        onLoadNodeChildren={handleLoadNodeChildren}
                        onRemoveTopic={handleRemoveTopic}
                        onToggleCard={handleToggleCard}
                        onCreateAssessment={() => {}}
                        onOpenCreateYourOwnWizard={handleOpenCreateYourOwnWizard}
                        onRemoveTopicsFromStudentCurriculum={() => {}}
                        onAddTopics={handleAddTopics}
                        onDeleteTenantCustomCurriculumTopics={
                            selectedStudentId &&
                            canDeleteSelectedTenantCustomCurriculumTopics(
                                state.selectedTopics,
                                state.studentCurriculumData,
                                selectedStudentId
                            )
                                ? handleOpenDeleteTenantCustomCurriculumTopics
                                : undefined
                        }
                        buildBreadcrumb={buildBreadcrumbForTopic}
                        getProviderName={getProviderName}
                        handleLoadTopicDetails={handleLoadTopicDetails}
                        setViewState={state.setViewState}
                        onClearAll={() => state.setSelectedTopics([])}
                        studentId={selectedStudentId}
                        isStudentLoading={isStudentLoading}
                        selectedStudentFirstName={selectedStudentFirstName}
                        onOpenUnitFromPlan={handleOpenUnitFromPlan}
                    />
                )}

                {/* Units Panels */}
                {state.activeTab === 'review' && (
                    <ReviewUnitsPanel
                        units={getUnitsForTab(state.activeTab, state.backendLessons)}
                        viewState={state.unitsViewState}
                        viewStates={unitsViewStates}
                        onApproveUnit={handleApproveUnit}
                        onDeleteUnit={handleDeleteUnit}
                        onRegenerateUnit={handleRegenerateUnit}
                        onRefresh={handleRefresh}
                        onMarkUnitTouched={handleMarkUnitTouched}
                        students={students}
                        isStudentLoading={isStudentLoading}
                        lessonUnitFocus={lessonUnitFocus}
                        onConsumedLessonUnitFocus={clearLessonUnitFocus}
                    />
                )}

                {state.activeTab === 'inprogress' && (
                    <InProgressUnitsPanel
                        units={getUnitsForTab(state.activeTab, state.backendLessons)}
                        viewState={state.unitsViewState}
                        viewStates={unitsViewStates}
                        onCreateAssessment={handleCreateAssessment}
                        onRefresh={handleRefresh}
                        students={students}
                        isStudentLoading={isStudentLoading}
                        selectedStudentId={selectedStudentId}
                        passFailThreshold={selectedStudentPassFailThreshold}
                        lessonUnitFocus={lessonUnitFocus}
                        onConsumedLessonUnitFocus={clearLessonUnitFocus}
                        onSessionViewTitleChange={setSessionViewLessonTitle}
                    />
                )}

                {state.activeTab === 'done' && (
                    <CompletedUnitsPanel
                        units={getUnitsForTab(state.activeTab, state.backendLessons)}
                        viewState={state.unitsViewState}
                        viewStates={unitsViewStates}
                        students={students}
                        isStudentLoading={isStudentLoading}
                        passFailThreshold={selectedStudentPassFailThreshold}
                        lessonUnitFocus={lessonUnitFocus}
                        onConsumedLessonUnitFocus={clearLessonUnitFocus}
                        onRefresh={handleRefresh}
                    />
                )}

                <CreateUnitWizard
                    open={showCreateUnitWizard}
                    onClose={() => setShowCreateUnitWizard(false)}
                    studentId={selectedStudentId}
                    studentCurriculumData={state.studentCurriculumData}
                    onCreateUnit={handleCreateUnit}
                    onLoadYearTopics={nodeId => handleLoadNodeChildren(nodeId, 'year')}
                    loadedNodeIds={state.loadedNodeIds}
                    loadingNodeIds={state.loadingNodeIds}
                    initialUnitCreationMode={createUnitTreeSeed.mode}
                    initialTopics={createUnitTreeSeed.topics}
                />

                {/* Remove Topics Confirmation Dialog (for student curriculum) */}
                <RemoveTopicsFromStudentCurriculumDialog
                    open={showRemoveTopicsConfirmDialog}
                    onOpenChange={setShowRemoveTopicsConfirmDialog}
                    selectedTopics={getLeafTopics(state.selectedTopics)}
                    buildBreadcrumb={buildBreadcrumbForTopic}
                    onRemoveTopic={handleRemoveTopicById}
                    onConfirm={handleRemoveTopicsFromStudentCurriculum}
                    selectedStudentFirstName={selectedStudentFirstName}
                    isProcessing={isRemovingTopicsFromCurriculum}
                    onProcessingFailed={() => setIsRemovingTopicsFromCurriculum(false)}
                />

                <DeleteTenantCustomCurriculumTopicsDialog
                    open={showDeleteTenantCustomCurriculumTopicsDialog}
                    onOpenChange={setShowDeleteTenantCustomCurriculumTopicsDialog}
                    topicCount={getLeafTopics(state.selectedTopics).length}
                    onConfirm={handleConfirmDeleteTenantCustomCurriculumTopics}
                />

                {/* Add Topics wizard (multi-subject, batched add to student curriculum) */}
                {state.activeTab === 'addTopics' && selectedStudentId && (
                    <AddTopicsWizardDialog
                        showDialog={state.showAddTopicsDialog}
                        selectedTopics={state.selectedTopics}
                        onClose={() => {
                            state.setShowAddTopicsDialog(false);
                            state.setViewState(viewStates.curriculumReady);
                        }}
                        onRemoveTopicById={handleRemoveTopicById}
                        onClearSelection={() => state.setSelectedTopics([])}
                        buildBreadcrumb={buildBreadcrumbForTopic}
                        studentEducationSystemKey={selectedStudentEducationSystemKey}
                        selectedStudentFirstName={selectedStudentFirstName}
                        defaultBuiltInProviderKey={state.selectedProvider || 'oak'}
                        studentId={selectedStudentId}
                        onRunJob={handleRunAddTopicsJob}
                        onWizardBatchingChange={(active) => {
                            addTopicsWizardBatchingRef.current = active;
                        }}
                        onLoadEducationSystemDefinitions={loadEducationSystemDefinitions}
                        onReloadStudentCurriculum={() => {
                            void commands.sendGetStudentCurriculumCommand({
                                C260_StudentId: selectedStudentId
                            });
                        }}
                        onPrepareCurriculumTreeView={(placements) => {
                            pendingAddTopicsExpandPlacementsRef.current = placements;
                        }}
                        onViewStudentCurriculum={(placements) => {
                            pendingAddTopicsExpandPlacementsRef.current = placements;
                            skipCreateTabExpandClearRef.current = true;
                            state.setIsStudentCurriculumActive(true);
                            state.isStudentCurriculumActiveRef.current = true;
                            const curriculum = state.studentCurriculumData;
                            if (placements.length > 0 && curriculum?.e180_Subjects?.length) {
                                state.setExpandedNodes(
                                    computeStudentCurriculumExpandNodesForPlacements(
                                        curriculum,
                                        placements
                                    )
                                );
                            }
                            navigate('/app/lessons/create');
                            if (selectedStudentId) {
                                void commands.sendGetStudentCurriculumCommand({
                                    C260_StudentId: selectedStudentId
                                });
                            }
                        }}
                        extractTopicMetadata={(topicData: any) =>
                            extractTopicMetadata(
                                topicData,
                                topicData?.e179_ProviderKey === 'custom' ||
                                    topicData?.e180_ProviderKey === 'custom'
                                    ? state.tenantCustomCurriculumLibraryData
                                    : state.curriculumData
                            )
                        }
                    />
                )}

                {/* Create Your Own wizard: subject pick → optional C316 → topic mode → child dialogs */}
                {state.activeTab === 'addTopics' && (
                    <CreateYourOwnWizardDialog
                        showDialog={state.showCreateYourOwnWizard}
                        tenantCustomCurriculumLibraryData={state.tenantCustomCurriculumLibraryData}
                        onClose={handleCloseCreateYourOwnWizard}
                        onLoadEducationSystems={loadEducationSystems}
                        onEnsureSubject={handleEnsureCustomSubject}
                        onRefreshTenantCustomCurriculum={() => {
                            commands.sendGetTenantCustomCurriculumCommand({});
                        }}
                        onOpenSingleTopicDialog={handleOpenSingleTopicFromWizard}
                        onOpenBatchTopicsDialog={handleOpenBatchTopicsFromWizard}
                        onWizardActiveChange={(active) => {
                            createYourOwnWizardActiveRef.current = active;
                        }}
                    />
                )}

                {/* Custom topic: C263/E183 form + C262 single authored topic */}
                {state.activeTab === 'addTopics' && (
                    <AddCustomTopicDialog
                        showDialog={state.showAddCustomTopicDialog}
                        initialCatalogSubjectId={state.wizardInitialCatalogSubjectId || undefined}
                        hideSubjectPicker={Boolean(state.wizardInitialCatalogSubjectId)}
                        tenantCustomCurriculumLibraryData={state.tenantCustomCurriculumLibraryData}
                        onClose={handleCloseAddCustomTopicDialog}
                        onLoadFormData={() => {
                            commands.getAddCustomTopicFormData({});
                        }}
                        onSubmit={(cmd) => {
                            void commands.sendAddCustomTopicToTenantCurriculumCommand(cmd);
                        }}
                        onLoadEducationSystemDefinitions={loadEducationSystemDefinitions}
                        onRefreshTenantCustomCurriculum={() => {
                            commands.sendGetTenantCustomCurriculumCommand({});
                        }}
                    />
                )}

                {/* Custom topics: C297/C298 for an existing custom curriculum subject (separate from Add Topics → student curriculum) */}
                {state.activeTab === 'addTopics' && (
                    <CreateCustomTopicsDialog
                        showDialog={state.showCreateCustomTopicsDialog}
                        initialCatalogSubjectId={state.wizardInitialCatalogSubjectId || undefined}
                        hideSubjectPicker={Boolean(state.wizardInitialCatalogSubjectId)}
                        tenantCustomCurriculumLibraryData={state.tenantCustomCurriculumLibraryData}
                        onClose={handleCloseCreateCustomTopicsDialog}
                        onGetTopicNames={handleGetCustomSubjectTopicNames}
                        onAddTopicsBatch={handleAddCustomSubjectTopicsBatch}
                        onLoadEducationSystemDefinitions={loadEducationSystemDefinitions}
                        onRefreshTenantCustomCurriculum={() => {
                            commands.sendGetTenantCustomCurriculumCommand({});
                        }}
                    />
                )}

                {/* Topic Limit Dialog */}
                <TopicLimitDialog
                    viewState={state.showLimitDialog ? topicLimitDialogViewStates.open : topicLimitDialogViewStates.closed}
                    viewStates={topicLimitDialogViewStates}
                    message={topicLimitMessage}
                    onClose={() => {
                        state.setShowLimitDialog(false);
                        setTopicLimitMessage(undefined);
                    }}
                />

                {/* Create Assessment Form Modal - Available on all tabs */}
                <CreateAssessmentForm
                    showForm={state.showCreateAssessmentForm}
                    selectedTopics={state.selectedTopics}
                    unitId={state.assessmentFormContext?.unitId}
                    topicIds={state.assessmentFormContext?.topicIds}
                    studentId={state.assessmentFormContext?.studentId || selectedStudentId}
                    unitTitle={state.assessmentFormContext?.unitTitle}
                    onClose={() => {
                        state.setShowCreateAssessmentForm(false);
                        state.setAssessmentFormContext(null);
                    }}
                    onCreateAssessment={() => {
                        state.setShowCreateAssessmentForm(false);
                        state.setAssessmentFormContext(null);
                    }}
                    buildBreadcrumb={buildBreadcrumbForTopic}
                    onRemoveTopic={handleRemoveTopic}
                    hasAttemptedSubmit={state.hasAttemptedSubmit}
                />
            </div>
    );
}
